import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import QrScanner from "react-qr-scanner";
import "../styles/VerifyCertificate.css";
import Config from "../config.js";

/**
 * Certificate Verification Component with Revocation System
 * 
 * To revoke a certificate:
 * 1. Add the encoded data part (after "data=") to the REVOKED_CERTIFICATE_DATA array below
 * 2. The system will check this list before making blockchain requests
 * 3. Revoked certificates will show "🚫 Dieses Zertifikat wurde widerrufen und ist ungültig."
 * 
 * Example: For URL "https://portal.auraplan.com/verify?data=ABC123"
 * Add "ABC123" to the REVOKED_CERTIFICATE_DATA array
 * 
 * Current revoked certificates: 14 total (2 original + 12 new ungültig)
 */

// ✅ Simple fallback for old barcodes
const decodeBase64WithFallback = (encodedData) => {
  try {
    // Try new UTF-8 method first
    const binaryString = atob(encodedData);
    return decodeURIComponent(escape(binaryString));
  } catch (error) {
    // Fallback to old method for legacy barcodes
    return atob(encodedData);
  }
};

const VerifyCertificate = () => {
  const [loading, setLoading] = useState(false);
  const [certificate, setCertificate] = useState(null);
  const [error, setError] = useState("");
  const [showScanner, setShowScanner] = useState(false);

  const navigate = useNavigate();

  // 🚫 Revoked certificate data - Add new revoked certificates here
  const REVOKED_CERTIFICATE_DATA = [
    // Original revoked certificates
    "RWx2aXMgQnJkYXJ8MjMuMDYuMTk4NXw0YTFjY2MwNjExMmI2MTE0ZTQ3OWRhODlmNDIxMGEyYTg5MWY4YWE0NWQ4ODE1YjVjM2M4YmJiZjY5MGZmOWMw",
    "RWx2aXMgQnJkYXJ8MjMuMDYuMTk4NXwzYWRhYzg4OGU0ZDE4MGE5Yjc3M2IyYjFkZDM3ZDJmOWRmNDQxNTYwZTE5Y2EzZDExOWE4NDNhNjU1N2IxNDgz",
    
    // New revoked certificates (ungültig)
    "RGFuaWplbCBQZXJha3wyNi4xMS4xOTc2fDdhY2YyZDY2M2UyZjc4YmUyMGNiYjc0Nzg5YTVkZTI4NTc5Y2RkMDY3NGM2ZThhYTVjNjljYTRiNzk1OTRmMTA",
    "TmVtYW5qYSBSYWRvdmljfDE2LjEyLjE5OTd8MDY1YTFjM2M2NWQ3NjkyN2VlMjM2YzM1NTI3NzQ1NGFiMmNiMDVjOTUxMjE4N2MxNTgzYzE3ODQxMjk4MTFjMw",
    "VGlob21pciBEYXV0b3ZpY3wxNy4xMi4xOTc3fGMxYjkyM2RiMmFlMmMzNTJkMjdiYjBiYTRjYjI0ZDFiZTIyOTY4YTY3ZDkwZTQzNjNjMThlNTJjMDNhOGQ2N2U",
    "U2FzYSBDdmpldGtvdmljfDIxLjAzLjE5OTd8NjJkMTk5YWQ4ZmJlZGVhZmU3NzAyZmRjMTlmM2E5ZjQzNTI0MWE4ZGY4ZjhlNTc2MWI3NTJlODQ5ZWZiOWEwYg",
    "U2FzYSBNYXRvc3wwNi4wOC4xOTc4fDI2YmRiN2Q2NjU3OGZjZjY0YzZiNmQwMTcxNDJhMTE3YmM2NTEwOTdhOWY5ZmU5YjU1NmIzNDM5NzljYzUwY2Y",
    "QnJ1bm8gS2VzdGVufDA1LjAyLjE5ODF8ODFjMDczYTBkZTI2NjMzNzM0ZWMzN2RiZmIzYjM3NWY1OWNmNDdiNWVkMmYyYTllZDg1NThlMDEzMzIxMWFlNQ",
    "S3J1bm9zbGF2IFBhamRha3wxMS4xMC4xOTY5fGM2M2UxNmYwYjQyZGFlYjdmOGU3NTNkMDM3N2Y3ZDhkY2U1MzI4NmEyM2I3Y2Y4YzY1Zjc3NmEwODc3ZjdiMWM",
    "VGlob21pciBEYXV0b3ZpY3wxNy4xMi4xOTc3fDI1ZWJhN2Q3MmEwYmZkY2YwMWJkNzEzMTc4MGZhZGMxY2ZlOWRkN2ExMjdlYTMzZTQ0ZjFmYWIyMWY1NGEyMGQ",
    "U2FzYSBDdmpldGtvdmljfDIxLjAzLjE5OTd8YTQ1OTdkOGI1MjNiYzA3NTdhOGYzZmI3N2EwYmQ5ZTkwNzUwZDYyYzBiYTc0MTVjZTg4MDg5NjI3OTIwMzUyMg",
    "U3JiaXNsYXYgRHJhZ2ljfDA1LjA2LjE5Nzh8MDU0MjEyN2ZjY2VhZDFkZTFhYWM2NjIzMWYzZDg4YjVmMTBhMjY1ZjkwN2IxMDQ5OGE0Nzc4NGZkY2ZjNDA0Nw",
    "QnJ1bm8gS2VzdGVufDA1LjAyLjE5ODF8MjBiNmQ4N2FhMDQ5ZmIxZDM4Y2NmYmNmZTY3ZWM1YWNiZjNhNDMwNzVjOWVhMTE2NDJjNmI5OWFkZDg1ZjZiNA",
    "TmVtYW5qYSBSYWRvdmljfDE2LjEyLjE5OTd8MDVmYTExZTM4NjRkYzkxMzRjMWQyYjk4ZDQ4NTRiMmJmNDg0MjA3MWNmMzMyMWFkZTNlYmQzOTQwNjA5MWNkYw",
    "U3JiaXNsYXYgRHJhZ2ljfDA1LjA2LjE5Nzh8MGI0NWEzNTRjYzQwMDhlZDI2ZDI2YzUxZjEwN2NiYzhiMTliNzE3YzhiM2Q3Njg3OWYwZTlmNmJiMjViNDdiOA"
  ];

  // Check if certificate is revoked by encoded data
  const isCertificateRevoked = (encodedData) => {
    // Clean the encoded data (remove any URL parts)
    let cleanData = encodedData;
    if (encodedData.includes("data=")) {
      cleanData = encodedData.split("data=")[1];
    }
    if (encodedData.includes("https://portal.auraplan.com/verify?data=")) {
      cleanData = encodedData.replace("https://portal.auraplan.com/verify?data=", "");
    }
    
    console.log("🔍 Checking revocation for data:", cleanData);
    console.log("🚫 Revoked certificates:", REVOKED_CERTIFICATE_DATA);
    
    return REVOKED_CERTIFICATE_DATA.includes(cleanData);
  };

  useEffect(() => {
    verifyCertificate();
  }, []);

  const getCameraConstraints = () => {
    // Check if the device is a mobile phone
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    return isMobile ? { facingMode: { exact: "environment" } } : { facingMode: "user" };
  };
  const verifyCertificate = async (qrData = null) => {
    let encodedData = qrData || new URLSearchParams(window.location.search).get("data");

    if (!encodedData) {
      setCertificate(null);
      setError("❌ Keine Zertifikatsdaten gefunden.");
      return;
    }

    try {
      setLoading(true);
      setError(""); // ✅ Clear error before making new request

      // 🚫 Check if certificate is revoked BEFORE processing
      if (isCertificateRevoked(encodedData)) {
        console.log("🚫 Certificate is revoked:", encodedData);
        setCertificate(null);
        setError("🚫 Dieses Zertifikat wurde widerrufen und ist ungültig.");
        setLoading(false);
        return;
      }

      // ✅ Remove Base URL if present
      if (encodedData.startsWith("https://portal.auraplan.com/verify?data=")) {
        encodedData = encodedData.split("data=")[1]; // Extract only the encoded part
      }

      console.log("📌 Encoded Data:", encodedData);

      // ✅ Ensure Base64 string is valid
      encodedData = encodedData.replace(/[^A-Za-z0-9+/=]/g, "");
      while (encodedData.length % 4 !== 0) {
        encodedData += "=";
      }

      let decodedData;
      try {
        // ✅ Try multiple decoding methods for backward compatibility
        decodedData = decodeBase64WithFallback(encodedData);
      } catch (error) {
        console.error("❌ Base64 Decoding Error:", error);
        setCertificate(null);
        setError("⚠️ Ungültiger QR-Code. Bitte erneut scannen.");
        return;
      }

      console.log("📌 Decoded Data:", decodedData);
      const [issuedTo, dob, certificateHash] = decodedData.split("|");

      console.log("📌 Sending to Backend:", { issuedTo, dob, certificateHash });

      const response = await fetch(`${Config.API_BASE_URL}/verify-certificate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ certificate_hash: certificateHash }),
      });

      const data = await response.json();
      console.log("📌 Backend Response:", data);

      if (!data.verified) {
        setCertificate(null);
        setError("❌ Dieses Zertifikat existiert nicht oder ist ungültig.");
        return;
      }

      setCertificate({
        name: issuedTo,
        dob,
        type: data.data.certificateType,
        issueDate: data.data.issueDate,
        expirationDate: data.data.expirationDate,
      });
    } catch (err) {
      console.error("❌ Backend API Error:", err);
      setCertificate(null);
      setError("⚠️ Verbindungsfehler. Bitte versuchen Sie es später erneut.");
    } finally {
      setLoading(false);
      setShowScanner(false);
    }
  };

  return (
    <div className="verify-container">
      {/* ✅ Updated Header with Logo */}
      <header className="certificate-header">
        <img src="/auraplan_academy_icon.png" alt="Auraplan Logo" className="logo" />
        <h1>Überprüfung digitaler Zertifikate – Powered by Blockchain</h1>
      </header>

      <main className="verify-main">
        <p>Überprüfen Sie digitale Zertifikate sicher und transparent.</p>

        {showScanner ? (
          <div className="qr-scanner">
            <QrScanner
              delay={300}
              onScan={(data) => data && verifyCertificate(data.text)}
              onError={(err) => console.error(err)}
              style={{ width: "100%" }}
              constraints={{
                video: getCameraConstraints(), // Dynamically set the correct mode
              }}
            />
            <button className="close-scanner-btn" onClick={() => setShowScanner(false)}>✖ Schließen</button>
          </div>
        ) : (
          <button className="scan-btn" onClick={() => setShowScanner(true)}>📷 QR-Code scannen</button>
        )}

        {loading && <p className="loading">⏳ Überprüfung läuft...</p>}
        {error && <p className="error">{error}</p>}

        {certificate ? (
          <div className="certificate-card">
            <h3>
                <img src="/check.png" alt="Verifiziert" className="verified-icon" />
                Zertifikat Verifiziert
            </h3>
            <p><strong>Name:</strong> {certificate.name}</p>
            <p><strong>Geburtsdatum:</strong> {certificate.dob}</p>
            <p><strong>Typ:</strong> {certificate.type}</p>
            <p><strong>Ausgestellt:</strong> {certificate.issueDate}</p>
            <p><strong>Gültig bis:</strong> {certificate.expirationDate}</p>
            <div className="blockchain-note">
              🔗 Dieses digitale Zertifikat wurde mit Blockchain-Technologie gespeichert und ist manipulationssicher.
            </div>
          </div>
        ) : (
          !loading && <p className="info">Bitte scannen Sie ein Zertifikat.</p>
        )}
      </main>
    </div>
  );
};

export default VerifyCertificate;
